import { ButtplugClientDevice, GenericDeviceMessageAttributes } from 'buttplug';
import { SubmenuDivHandler } from './SubmenuDivHandler.js';
import { DeviceControlMode, DeviceRemoteStub, IntifaceController, SupportedAction, PeerId } from '../IntifaceController.js';
import { MenuController, Submenu } from './menu-controller.js';
import { Events } from '../network-events.js';
import { networkManager } from '@wonderlandcloud/client';
import { EventsClient, ProstheticType } from '../events-client.js';
import Logger from '../utils/logger.js';
import { applyComponentStyles } from '../utils/shared-styles.js';
import { UserSettingsStore } from '../utils/user-settings-store.js';
import {
    createDeviceMenuSchema,
    DEVICE_MENU_ACTIONS,
    DEVICE_MENU_LABELS,
    readDeviceSchemaButtonLabel,
    readDeviceSchemaInputPlaceholder,
} from './schemas/device-menu-schema.js';

const MODULE = 'DeviceSubmenu';

const CSS = `
        .mm-device-range-control {
            display: flex;
            align-items: center;
            gap: clamp(0.25rem, 1.5vw, 0.5rem);
            margin: 0.25rem 0;
            padding: clamp(0.25rem, 1.5vw, 0.5rem);
            border: 1px solid #00d9ff;
            border-radius: 10px;
            background: linear-gradient(135deg, rgba(0, 217, 255, 0.05) 0%, rgba(0, 255, 157, 0.05) 100%);
            flex-wrap: wrap;
            box-shadow: 0 0 15px rgba(0, 217, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05);
        }

        .mm-device-range-control input[type="range"] { 
            flex: 1;
            min-width: 120px;
            height: 36px;
            cursor: pointer;
            accent-color: #00ffff;
            background: rgba(0, 217, 255, 0.1);
            border-radius: 10px;
        }

        .mm-device-range-control input[type="text"] {
            width: clamp(3rem, 10vw, 4rem);
            text-align: center;
            font-size: clamp(0.875rem, 2.5vw, 1rem);
            font-weight: 600;
            padding: 0.5rem;
            border: 1px solid #00d9ff;
            border-radius: 6px;
            min-height: 40px;
            background: rgba(10, 15, 30, 0.8);
            color: #00ffff;
            box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 0 10px rgba(0, 217, 255, 0.2);
        }

        .mm-device-range-control input[type="text"]:focus {
            outline: none;
            border-color: #00ffff;
            box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 0 15px rgba(0, 255, 255, 0.5);
        }

        .mm-device-range-control input[type="checkbox"] {
            display: none;
        }

        /* Duration input fields */
        .mm-duration-input {
            flex: 1;
            min-width: 120px;
            text-align: center;
            font-size: clamp(0.875rem, 2.5vw, 1rem);
            font-weight: 600;
            padding: 0.5rem;
            border: 1px solid #00d9ff;
            border-radius: 6px;
            min-height: 40px;
            background: rgba(10, 15, 30, 0.8);
            color: #00ffff;
            box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 0 10px rgba(0, 217, 255, 0.2);
            transition: all 0.3s ease;
        }

        .mm-duration-input:focus {
            outline: none;
            border-color: #00ffff;
            box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 0 15px rgba(0, 255, 255, 0.5);
        }

        .mm-duration-input::placeholder {
            color: rgba(0, 217, 255, 0.5);
            font-size: 0.75rem;
        }

        .mm-countdown-input {
            width: 4.5rem;
            flex-shrink: 0;
            text-align: center;
            font-size: clamp(0.875rem, 2.5vw, 1rem);
            font-weight: 600;
            padding: 0.5rem;
            border: 1px solid #00ff9d;
            border-radius: 6px;
            min-height: 40px;
            background: rgba(0, 255, 157, 0.1);
            color: #00ff9d;
            box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 0 10px rgba(0, 255, 157, 0.3);
        }

        .mm-clockwise-label {
            cursor: pointer;
            padding: 0.375rem 0.75rem;
            border: 1px solid #00d9ff;
            border-radius: 6px;
            background: rgba(0, 217, 255, 0.1);
            color: #00ffff;
            font-size: clamp(0.75rem, 2vw, 0.875rem);
            font-weight: 600;
            transition: all 0.3s ease;
            user-select: none;
            white-space: nowrap;
            min-height: 40px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            box-shadow: 0 0 10px rgba(0, 217, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .mm-clockwise-label:hover {
            background: linear-gradient(135deg, rgba(0, 217, 255, 0.3) 0%, rgba(0, 255, 157, 0.3) 100%);
            box-shadow: 0 0 20px rgba(0, 217, 255, 0.4);
            transform: translateY(-1px);
        }

        .mm-device-range-control input[type="checkbox"]:checked + .mm-clockwise-label {
            background: linear-gradient(135deg, #00d9ff 0%, #00ff9d 100%);
            color: #0a0f1e;
            border-color: #00ffff;
            box-shadow: 0 0 20px rgba(0, 217, 255, 0.6), 0 0 30px rgba(0, 255, 157, 0.4);
        }

        .mm-device-subheader {
            display: flex;
            text-align: left;
            font-size: clamp(1rem, 3vw, 1.25rem);
            font-weight: 700;
            margin: 0.5rem 0 0.25rem 0;
            padding: 0.5rem 0.25rem;
            border-bottom: 2px solid #00d9ff;
            color: #00ffff;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            text-shadow: 0 0 10px rgba(0, 217, 255, 0.5);
        }
        
        .mm-flexdiv-container {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 0.25rem;
            flex-wrap: wrap;
        }

        /* Mobile adjustments for device controls */
        @media (max-width: 768px) {
            .mm-device-range-control {
                flex-direction: column;
                align-items: stretch;
                gap: 0.25rem;
            }

            .mm-device-range-control input[type="range"] {
                width: 100%;
                min-width: unset;
            }

            .mm-device-range-control input[type="text"] {
                width: 100%;
                max-width: 150px;
                margin: 0 auto;
            }

            .mm-duration-input {
                min-width: 100px;
            }

            .mm-countdown-input {
                width: 4rem;
            }
        }
`;

const MM_DEVICE_HTML = `
        <!-- Modal content for Device menu -->
        <div id="menu-modal-device" class="menu-modal-content">
            <h2>Device Menu</h2>
            <p>Intiface Central<br>
            URL:<input type="text" id="mm-device-intiface-url" class="mm-text-input" onkeydown="event.stopPropagation()">
            <div class="mm-flexdiv-container"><button id="mm-device-connect-button" class="mm-submit-button">Connect</button>
            <button id="mm-device-environment-button" class="mm-submit-button" hidden>Environment</button>
            <button id="mm-device-permit-button" class="mm-submit-button" hidden>Offer control</button>
            <button id="mm-device-revoke-button" class="mm-submit-button" hidden>Revoke permission to control</button>
            <button id="mm-device-disconnect-button" class="mm-submit-button" hidden>Disconnect controller</button>
            <button id="mm-device-leave-environment-button" class="mm-submit-button" hidden>Leave Environment</button>
            <button id="mm-device-haptic-display-toggle" class="mm-submit-button">Show Haptics HUD</button></div>
            <div class="mm-device-subheader">Prosthetic</div>
            <div class="mm-flexdiv-container">
                <button id="mm-device-prosthetic-none" class="mm-submit-button">None</button>
                <button id="mm-device-prosthetic-inny" class="mm-submit-button">Inny</button>
                <button id="mm-device-prosthetic-outy" class="mm-submit-button">Outy</button>
            </div>
            <div id="menu-modal-device-controls-container" class="menu-modal-content-holder">
        </div>
`;

const MM_DEVICE_HTML_REMOTE = `
        <!-- Modal content for Remote Device menu -->
        <div id="menu-modal-device" class="menu-modal-content">
            <h2>Remote Device Menu</h2>
            <div class="mm-flexdiv-container">
            <button id="mm-device-environment-button" class="mm-submit-button" hidden>Environment</button>
            <button id="mm-device-leave-environment-button" class="mm-submit-button" hidden>Leave Environment</button></div>
            <div id="menu-modal-device-controls-container" class="menu-modal-content-holder">
        </div>
`;

const DEFAULT_INTIFACE_URL = "ws://127.0.0.1:12345";

/**
 * DebugSubmenuDH
 */
export class DeviceSubmenuDH extends SubmenuDivHandler {
    static TypeName = 'DeviceSubmenuDH';

    intifaceURL: string = DEFAULT_INTIFACE_URL;

    isRemote: boolean = false;
    peerId?: PeerId;
    permissionGranted: boolean = false;

    deviceControlsModal: HTMLDivElement | undefined;
    deviceControlStatusText: HTMLInputElement | undefined;
    deviceControlStatusDiv: HTMLDivElement | undefined;
    requestedDurationInput: HTMLInputElement | undefined;
    private durationCountdownInput: HTMLInputElement | undefined;
    private durationCountdownTimer: number | undefined;
    deviceControlDivs: HTMLDivElement[] = []; // store references to all created divs (for each haptic device)
    controllableDevicesMap: Map<ButtplugClientDevice | DeviceRemoteStub, Map<SupportedAction, HTMLInputElement[]>> | undefined;
    controllableAddonMap: Map<ButtplugClientDevice | DeviceRemoteStub, Map<SupportedAction, HTMLInputElement[]>> | undefined;
    
    // Track event listeners for cleanup
    private eventListeners: Array<{
        element: HTMLElement;
        event: string;
        handler: EventListener;
    }> = [];

    /**
     * Add an event listener and track it for cleanup.
     * @param element The element to attach the listener to
     * @param event The event name
     * @param handler The event handler function
     */
    private addTrackedEventListener(element: HTMLElement, event: string, handler: EventListener): void {
        element.addEventListener(event, handler);
        this.eventListeners.push({ element, event, handler });
    }

    /**
     * Remove all tracked event listeners.
     */
    private removeAllEventListeners(): void {
        for (const { element, event, handler } of this.eventListeners) {
            element.removeEventListener(event, handler);
        }
        this.eventListeners = [];
    }

    /**
     * Set the display and disabled state for multiple buttons at once.
     * @param buttonConfigs Array of button configurations
     */
    private setButtonStates(buttonConfigs: Array<{
        buttonId: string;
        display: 'flex' | 'none';
        disabled?: boolean;
        innerHTML?: string;
    }>): void {
        for (const config of buttonConfigs) {
            const button = document.getElementById(config.buttonId) as HTMLButtonElement;
            if (button) {
                button.style.display = config.display;
                if (config.disabled !== undefined) {
                    button.disabled = config.disabled;
                }
                if (config.innerHTML !== undefined) {
                    button.innerHTML = config.innerHTML;
                }
            }
        }
    }

    constructor(isRemote: boolean = false, peerId?: PeerId) {
        super();

        this.isRemote = isRemote;

        if(this.isRemote) {
            if(peerId === undefined) {
                Logger.error(MODULE, "PeerId property required when IsRemote set to true");
            }
            this.peerId = peerId;
        } else {
            this.intifaceURL = UserSettingsStore.getCookie(UserSettingsStore.Keys.IntifaceUrl);
            if (!this.intifaceURL || this.intifaceURL == "") {
                this.intifaceURL = DEFAULT_INTIFACE_URL;
            }
        }
        // Apply device-specific styles (shared styles applied by MenuController)
        applyComponentStyles(CSS, 'device-submenu');
    }

    updateConditionals(): void {
        // Remote submenus don't have conditional UI elements
        if (!this.isRemote) {
            this.updateConnectButtonLabel();
            this.updateHapticDisplayToggleLabel();
            this.updateProstheticButtons();
        }

        this.updateDeviceControlButtons();
    }

    private updateHapticDisplayToggleLabel(): void {
        const toggleButton = document.getElementById('mm-device-haptic-display-toggle') as HTMLButtonElement;
        if (!toggleButton) return;

        const enabled = EventsClient.instance?.isHapticDisplayEnabled?.() ?? true;
        toggleButton.innerHTML = enabled ? 'Hide Haptics HUD' : 'Show Haptics HUD';
    }

    private setProstheticButtonLabel(buttonId: string, baseLabel: string, active: boolean): void {
        const button = document.getElementById(buttonId) as HTMLButtonElement | null;
        if (!button) {
            return;
        }
        button.innerHTML = active ? `${baseLabel} (Active)` : baseLabel;
    }

    private updateProstheticButtons(): void {
        const state = EventsClient.instance?.getLocalProstheticState?.() ?? { type: 'none' as ProstheticType };
        this.setProstheticButtonLabel('mm-device-prosthetic-none', 'None', state.type === 'none');
        this.setProstheticButtonLabel('mm-device-prosthetic-inny', 'Inny', state.type === 'inny');
        this.setProstheticButtonLabel('mm-device-prosthetic-outy', 'Outy', state.type === 'outy');
    }

    /**
     * Update the connect button label based on connection state.
     */
    private updateConnectButtonLabel(): void {
        const btnConnectToIntiface = document.getElementById('mm-device-connect-button') as HTMLButtonElement;
        if (!btnConnectToIntiface) return;

        const deviceSchema = createDeviceMenuSchema();

        if (IntifaceController.instance.connected) {
            btnConnectToIntiface.innerHTML = DEVICE_MENU_LABELS.disconnect;
        } else if (IntifaceController.instance.connecting) {
            btnConnectToIntiface.innerHTML = DEVICE_MENU_LABELS.connecting;
        } else {
            btnConnectToIntiface.innerHTML = readDeviceSchemaButtonLabel(
                deviceSchema,
                DEVICE_MENU_ACTIONS.connectToIntiface,
                DEVICE_MENU_LABELS.connect,
            );
        }
    }

    /**
     * Update device control buttons based on connection state and control mode.
     */
    private updateDeviceControlButtons(): void {
        if (!IntifaceController.instance.connected && !this.isRemote) {
            this.updateButtonsWhenDisconnected();
            return;
        }

        const mode = IntifaceController.instance.deviceControlMode;
        switch (mode) {
            case DeviceControlMode.Self:
                this.updateButtonsForSelfMode();
                break;
            case DeviceControlMode.Receiver:
                this.updateButtonsForReceiverMode();
                break;
            case DeviceControlMode.Environment:
                this.updateButtonsForEnvironmentMode();
                break;
        }
    }

    /**
     * Update buttons when not connected to Intiface Central.
     */
    private updateButtonsWhenDisconnected(): void {
        this.setButtonStates([
            { buttonId: 'mm-device-permit-button', display: 'none', disabled: true },
            { buttonId: 'mm-device-revoke-button', display: 'none', disabled: true },
            { buttonId: 'mm-device-disconnect-button', display: 'none', disabled: true },
            { buttonId: 'mm-device-environment-button', display: 'none', disabled: true },
            { buttonId: 'mm-device-leave-environment-button', display: 'none', disabled: true }
        ]);
    }

    /**
     * Update buttons when in Self control mode (controlling own devices).
     */
    private updateButtonsForSelfMode(): void {
        this.setButtonStates([
            { buttonId: 'mm-device-permit-button', display: 'flex', disabled: false, innerHTML: "Offer control" },
            { buttonId: 'mm-device-revoke-button', display: 'none' },
            { buttonId: 'mm-device-disconnect-button', display: 'none' },
            { buttonId: 'mm-device-environment-button', display: 'flex', disabled: false },
            { buttonId: 'mm-device-leave-environment-button', display: 'none', disabled: false }
        ]);
    }

    /**
     * Update buttons when in Receiver mode (remote peer controlling local devices).
     */
    private updateButtonsForReceiverMode(): void {
        const controllingPeerId = IntifaceController.instance.controllingPeerId;
        
        if (controllingPeerId !== null && controllingPeerId >= 0) {
            // Controller actively connected
            this.setButtonStates([
                { buttonId: 'mm-device-permit-button', display: 'none' },
                { buttonId: 'mm-device-revoke-button', display: 'none' },
                { buttonId: 'mm-device-disconnect-button', display: 'flex', disabled: false },
                { buttonId: 'mm-device-environment-button', display: 'none' },
                { buttonId: 'mm-device-leave-environment-button', display: 'none' }
            ]);
        } else if (this.permissionGranted) {
            // Permission granted but controller not yet connected
            this.setButtonStates([
                { buttonId: 'mm-device-permit-button', display: 'flex', disabled: false, innerHTML: "Regenerate key" },
                { buttonId: 'mm-device-revoke-button', display: 'flex', disabled: false },
                { buttonId: 'mm-device-disconnect-button', display: 'none' },
                { buttonId: 'mm-device-environment-button', display: 'none' },
                { buttonId: 'mm-device-leave-environment-button', display: 'none' }
            ]);
        } else {
            // Receiver mode but permission not yet granted
            this.setButtonStates([
                { buttonId: 'mm-device-permit-button', display: 'flex', disabled: false, innerHTML: "Offer control" },
                { buttonId: 'mm-device-revoke-button', display: 'none' },
                { buttonId: 'mm-device-disconnect-button', display: 'none' },
                { buttonId: 'mm-device-environment-button', display: 'none' },
                { buttonId: 'mm-device-leave-environment-button', display: 'none' }
            ]);
        }
        
        this.updateDeviceControls();
    }

    /**
     * Update buttons when in Environment mode (environmental control of devices).
     */
    private updateButtonsForEnvironmentMode(): void {
        this.setButtonStates([
            { buttonId: 'mm-device-permit-button', display: 'none' },
            { buttonId: 'mm-device-revoke-button', display: 'none' },
            { buttonId: 'mm-device-disconnect-button', display: 'none' },
            { buttonId: 'mm-device-environment-button', display: 'none' },
            { buttonId: 'mm-device-leave-environment-button', display: 'flex', disabled: false }
        ]);
        this.updateDeviceControls();
    }

    displayMenuContent(containerDiv: HTMLDivElement): void {
        if(this.isRemote) {
            containerDiv.innerHTML = MM_DEVICE_HTML_REMOTE;

            const btnEnvironment = document.getElementById('mm-device-environment-button') as HTMLButtonElement;
            const btnLeaveEnvironment = document.getElementById('mm-device-leave-environment-button') as HTMLButtonElement;

            if ( !btnEnvironment || !btnLeaveEnvironment ) {
                Logger.error(MODULE, 'Menu Controller could not find one or more named resources in current menu content. These html objects should have been injected by this class at runtime.');
                return;
            }

            this.addTrackedEventListener(btnEnvironment, 'click', () => {
                (IntifaceController.instance as any)._deviceControlMode = DeviceControlMode.Environment;
                IntifaceController.instance.onControlModeChanged.notify();
                this.updateDeviceControlButtons();
            });

            this.addTrackedEventListener(btnLeaveEnvironment, 'click', () => {
                (IntifaceController.instance as any)._deviceControlMode = DeviceControlMode.Self;
                IntifaceController.instance.onControlModeChanged.notify();
                this.updateDeviceControlButtons();
            });

        } else {
            containerDiv.innerHTML = MM_DEVICE_HTML;
            const deviceSchema = createDeviceMenuSchema();

            const btnConnectToIntiface = document.getElementById('mm-device-connect-button');
            const btnEnvironment = document.getElementById('mm-device-environment-button') as HTMLButtonElement;
            const inputIntifaceURL = document.getElementById('mm-device-intiface-url') as HTMLInputElement;
            const btnPermitDeviceControl = document.getElementById('mm-device-permit-button') as HTMLButtonElement;
            const btnRevokeDeviceControl = document.getElementById('mm-device-revoke-button') as HTMLButtonElement;
            const btnDisconnectController = document.getElementById('mm-device-disconnect-button') as HTMLButtonElement;
            const btnLeaveEnvironment = document.getElementById('mm-device-leave-environment-button') as HTMLButtonElement;
            const btnToggleHapticDisplay = document.getElementById('mm-device-haptic-display-toggle') as HTMLButtonElement;
            const btnProstheticNone = document.getElementById('mm-device-prosthetic-none') as HTMLButtonElement;
            const btnProstheticInny = document.getElementById('mm-device-prosthetic-inny') as HTMLButtonElement;
            const btnProstheticOuty = document.getElementById('mm-device-prosthetic-outy') as HTMLButtonElement;


            if (!btnConnectToIntiface || !inputIntifaceURL || !btnPermitDeviceControl || !btnRevokeDeviceControl || !btnDisconnectController || !btnEnvironment || !btnLeaveEnvironment || !btnToggleHapticDisplay || !btnProstheticNone || !btnProstheticInny || !btnProstheticOuty) {
                Logger.error(MODULE, 'Menu Controller could not find one or more named resources in current menu content. These html objects should have been injected by this class at runtime.');
                return;
            }

            const titleEl = containerDiv.querySelector('h2');
            if (titleEl) {
                titleEl.textContent = deviceSchema.title;
            }
            btnConnectToIntiface.textContent = readDeviceSchemaButtonLabel(
                deviceSchema,
                DEVICE_MENU_ACTIONS.connectToIntiface,
                DEVICE_MENU_LABELS.connect,
            );
            btnPermitDeviceControl.textContent = readDeviceSchemaButtonLabel(
                deviceSchema,
                'device-permit-remote-control',
                DEVICE_MENU_LABELS.permitRemoteControl,
            );
            btnRevokeDeviceControl.textContent = readDeviceSchemaButtonLabel(
                deviceSchema,
                'device-revoke-remote-control',
                DEVICE_MENU_LABELS.revokeRemoteControl,
            );
            inputIntifaceURL.placeholder = readDeviceSchemaInputPlaceholder(
                deviceSchema,
                'device-intiface-url-input',
                DEVICE_MENU_LABELS.intifaceUrlPlaceholder,
            );

            inputIntifaceURL.value = this.intifaceURL;

            this.addTrackedEventListener(btnConnectToIntiface, 'click', () => {
                UserSettingsStore.setCookie(UserSettingsStore.Keys.IntifaceUrl, inputIntifaceURL.value, 365);

                if (IntifaceController.instance.connected) {
                    MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.disconnectFromIntiface, { url: inputIntifaceURL.value });
                } else if (!IntifaceController.instance.connecting) {
                    MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.connectToIntiface, { url: inputIntifaceURL.value });
                }
            });

            this.addTrackedEventListener(btnPermitDeviceControl, 'click', () => {
                if (IntifaceController.instance.connected) {
                    IntifaceController.instance.ShareDeviceControls();
                }
            });

            this.addTrackedEventListener(btnEnvironment, 'click', () => {
                if (IntifaceController.instance.connected) {
                    (IntifaceController.instance as any)._deviceControlMode = DeviceControlMode.Environment;
                    IntifaceController.instance.onControlModeChanged.notify();
                    this.updateDeviceControlButtons();
                }
            });

            this.addTrackedEventListener(btnLeaveEnvironment, 'click', () => {
                if (IntifaceController.instance.connected) {
                    (IntifaceController.instance as any)._deviceControlMode = DeviceControlMode.Self;
                    IntifaceController.instance.onControlModeChanged.notify();
                    this.updateDeviceControlButtons();
                }
            });

            this.addTrackedEventListener(btnToggleHapticDisplay, 'click', () => {
                const currentlyEnabled = EventsClient.instance?.isHapticDisplayEnabled?.() ?? true;
                EventsClient.instance?.setHapticDisplayEnabled?.(!currentlyEnabled);
                this.updateHapticDisplayToggleLabel();
            });

            this.addTrackedEventListener(btnProstheticNone, 'click', () => {
                EventsClient.instance?.requestProstheticSelection?.('none');
                this.updateProstheticButtons();
            });

            this.addTrackedEventListener(btnProstheticInny, 'click', () => {
                EventsClient.instance?.requestProstheticSelection?.('inny');
                this.updateProstheticButtons();
            });

            this.addTrackedEventListener(btnProstheticOuty, 'click', () => {
                EventsClient.instance?.requestProstheticSelection?.('outy');
                this.updateProstheticButtons();
            });

            this.addTrackedEventListener(btnRevokeDeviceControl, 'click', () => {
                if (IntifaceController.instance.connected && IntifaceController.instance.deviceControlMode == DeviceControlMode.Receiver) {
                    this.revokeDeviceControl();
                }
            });

            this.addTrackedEventListener(btnDisconnectController, 'click', () => {
                if (IntifaceController.instance.connected && IntifaceController.instance.deviceControlMode == DeviceControlMode.Receiver) {
                    this.disconnectController();
                }
            });

            // Listen for permission success to update button state
            this.setupPermissionListener();
            this.updateHapticDisplayToggleLabel();
            this.updateProstheticButtons();
        }
        this.deviceControlsModal = document.getElementById('menu-modal-device-controls-container') as HTMLDivElement;

        this.updateDeviceControls();
    }

    /**
     * Generate and display device control UI for all available devices.
     * Creates range sliders, text inputs, and buttons for each actuator type on each device.
     * 
     * @remarks
     * Only generates UI for Self mode or remote device submenus.
     * Receiver mode does not show controls (remote controller has control).
     * 
     * UI generation flow:
     * 1. Cleanup existing controls and DOM elements
     * 2. Iterate through all devices (local or remote based on context)
     * 3. For each device, generate controls for available actuator types:
     *    - Vibrate: Range sliders for each vibrator
     *    - Rotate: Range sliders + clockwise checkboxes for each rotator
     *    - Scalar: Range sliders for each scalar actuator
     * 4. Restore last command values to controls (shows current/last state)
     * 5. Track latest command end time to display countdown timer
     * 6. Generate footer with duration input, Send button, Stop button
     * 
     * Control state restoration:
     * - Reads last command from deviceLastCommandMap (local) or DeviceRemoteStub (remote)
     * - Sets range/text values to last actuateVals if available
     * - Calculates remaining time for timed commands
     * 
     * @example
     * // Refresh device controls after connection state change
     * MenuController.instance.updateDeviceControls();
     */
    updateDeviceControls(): void {
        const mode = IntifaceController.instance.deviceControlMode;
        if (this.isRemote || mode == DeviceControlMode.Self || mode == DeviceControlMode.Environment) {
            this.cleanupDeviceControls();

            // Track overall latest end time across any device/action to display a single countdown
            let latestEndMs: number = 0;

            for (const device of IntifaceController.instance.getDevices(this.peerId)) {
                Logger.debug(MODULE, "Updating device controls for device: " + device.name);
                const deviceHeader = document.createElement('div');
                deviceHeader.classList.add('mm-device-subheader');
                deviceHeader.innerHTML = String(device.name);
                this.deviceControlsModal?.appendChild(deviceHeader);

                // Ensure device has entries in control maps (reuse existing or create new)
                let deviceInputMap = this.controllableDevicesMap?.get(device);
                if (!deviceInputMap) {
                    deviceInputMap = new Map<SupportedAction, HTMLInputElement[]>();
                    this.controllableDevicesMap?.set(device, deviceInputMap);
                }
                let deviceAddonMap = this.controllableAddonMap?.get(device);
                if (!deviceAddonMap) {
                    deviceAddonMap = new Map<SupportedAction, HTMLInputElement[]>();
                    this.controllableAddonMap?.set(device, deviceAddonMap);
                }

                /* Handle Vibrate actions */
                latestEndMs = this.createVibrateControls(device, latestEndMs);
                /* End Vibrate */

                /* Handle Rotate actions */
                latestEndMs = this.createRotateControls(device, latestEndMs);
                /* End Rotate */

                /* Handle Scalar actions */
                latestEndMs = this.createScalarControls(device, latestEndMs);
                /* End Scalar */

            }

            if (this.deviceControlDivs.length > 0) {
                this.requestedDurationInput = document.createElement('input');
                this.requestedDurationInput.type = 'text';
                this.requestedDurationInput.classList.add('mm-duration-input');
                this.requestedDurationInput.value = '';
                this.requestedDurationInput.placeholder = 'blank = persistent';
                // Countdown display (readonly), only shown if a prior timed command is active
                this.durationCountdownInput = document.createElement('input');
                this.durationCountdownInput.type = 'text';
                this.durationCountdownInput.classList.add('mm-countdown-input');
                this.durationCountdownInput.readOnly = true;
                this.durationCountdownInput.style.display = 'none';

                if (IntifaceController.instance.deviceControlMode != DeviceControlMode.Environment) {
                    const durationDiv: HTMLDivElement = document.createElement('div');
                    durationDiv.classList.add('mm-device-range-control');
                    durationDiv.innerHTML = "Duration: ";
                    durationDiv.appendChild(this.requestedDurationInput);
                    durationDiv.appendChild(this.durationCountdownInput);
                    this.deviceControlsModal?.appendChild(durationDiv);
                    this.deviceControlDivs.push(durationDiv);

                    const submitDeviceRequestButton = document.createElement('input');
                    submitDeviceRequestButton.type = 'button';
                    submitDeviceRequestButton.classList.add('mm-submit-button');
                    submitDeviceRequestButton.value = DEVICE_MENU_LABELS.send;

                    submitDeviceRequestButton.addEventListener('click', () => {
                        if (MenuController.instance.currentSubmenu == Submenu.Device || MenuController.instance.currentSubmenu == Submenu.RemoteDeviceClone) {
                            this.sendDeviceCommands();
                        }
                    });

                    const stopDeviceRequestButton = document.createElement('input');
                    stopDeviceRequestButton.type = 'button';
                    stopDeviceRequestButton.classList.add('mm-submit-button');
                    stopDeviceRequestButton.value = DEVICE_MENU_LABELS.stop;
                    stopDeviceRequestButton.addEventListener('click', () => {
                        if (MenuController.instance.currentSubmenu == Submenu.Device || MenuController.instance.currentSubmenu == Submenu.RemoteDeviceClone) {
                            this.stopDeviceCommands();
                        }
                    });

                    const deviceFooter: HTMLDivElement = document.createElement('div');
                    deviceFooter.classList.add('menu-modal-content');

                    deviceFooter.appendChild(submitDeviceRequestButton);
                    deviceFooter.appendChild(stopDeviceRequestButton);

                    this.deviceControlDivs.push(deviceFooter);
                    this.deviceControlsModal?.appendChild(deviceFooter);
                }

                // Initialize countdown visibility/timer if a timed command is active
                const initCountdown = (endMs: number) => {
                    if (!this.durationCountdownInput) return;
                    const update = () => {
                        const remain = Math.max(0, Math.ceil((endMs - Date.now()) / 1000));
                        if (remain > 0) {
                            this.durationCountdownInput!.value = `${remain}s`;
                            this.durationCountdownInput!.style.display = '';
                        } else {
                            this.durationCountdownInput!.style.display = 'none';
                            if (this.durationCountdownTimer) {
                                clearInterval(this.durationCountdownTimer);
                                this.durationCountdownTimer = undefined;
                            }
                        }
                    };
                    update();
                    if (this.durationCountdownTimer) clearInterval(this.durationCountdownTimer);
                    this.durationCountdownTimer = window.setInterval(update, 1000);
                };

                if (latestEndMs > Date.now()) {
                    initCountdown(latestEndMs);
                }
            } else if (IntifaceController.instance.connected || this.isRemote) {
                const deviceFooter: HTMLDivElement = document.createElement('div');
                deviceFooter.classList.add('mm-device-subheader');
                deviceFooter.innerHTML = "No Devices Detected";

                this.deviceControlDivs.push(deviceFooter);
                this.deviceControlsModal?.appendChild(deviceFooter);
            }

        } else if (IntifaceController.instance.deviceControlMode == DeviceControlMode.Receiver) { //Receiver mode
            if (this.deviceControlDivs.length > 0) {
                this.cleanupDeviceControls();
            }

            // Check if a controller is connected
            const controllingPeerId = IntifaceController.instance.controllingPeerId;
            
            if (controllingPeerId !== null) {
                // Show "Controlled by Peer ID: X" message
                this.deviceControlStatusDiv = document.getElementById('mm-device-permit-status-container') as HTMLDivElement;
                this.deviceControlStatusText = document.getElementById('mm-device-permit-status-key') as HTMLInputElement;
                
                if (!this.deviceControlStatusDiv || !this.deviceControlStatusText) {
                    const deviceControlStatusDiv = document.createElement('div');
                    deviceControlStatusDiv.classList.add('mm-device-subheader');
                    deviceControlStatusDiv.id = 'mm-device-permit-status-container';
                    deviceControlStatusDiv.innerHTML = "Controlled by Peer ID: ";
                    const dispBox = document.createElement('input');
                    dispBox.type = 'text';
                    dispBox.id = 'mm-device-permit-status-key';
                    dispBox.readOnly = true;
                    this.deviceControlsModal?.appendChild(deviceControlStatusDiv);
                    deviceControlStatusDiv.appendChild(dispBox);
                    this.deviceControlStatusText = dispBox;
                    this.deviceControlStatusDiv = deviceControlStatusDiv;
                } else {
                    // Update the label text
                    const labelText = this.deviceControlStatusDiv.childNodes[0];
                    if (labelText && labelText.nodeType === Node.TEXT_NODE) {
                        labelText.textContent = "Controlled by Peer ID: ";
                    }
                }

                this.deviceControlStatusText.value = controllingPeerId.toString();
                this.deviceControlStatusDiv.style.display = '';
            } else if (this.permissionGranted) {
                // Show control key if permission granted but not yet connected
                this.deviceControlStatusText = document.getElementById('mm-device-permit-status-key') as HTMLInputElement;
                this.deviceControlStatusDiv = document.getElementById('mm-device-permit-status-container') as HTMLDivElement;
                
                if (!this.deviceControlStatusText || !this.deviceControlStatusDiv) {
                    const deviceControlStatusDiv = document.createElement('div');
                    deviceControlStatusDiv.classList.add('mm-device-subheader');
                    deviceControlStatusDiv.id = 'mm-device-permit-status-container';
                    deviceControlStatusDiv.innerHTML = "Control Key: ";
                    const dispBox = document.createElement('input');
                    dispBox.type = 'text';
                    dispBox.classList.add('mm-text-input');
                    dispBox.value = '0';
                    dispBox.id = 'mm-device-permit-status-key';
                    dispBox.readOnly = true;
                    this.deviceControlsModal?.appendChild(deviceControlStatusDiv);
                    deviceControlStatusDiv.appendChild(dispBox);
                    this.deviceControlStatusText = dispBox;
                    this.deviceControlStatusDiv = deviceControlStatusDiv;
                } else {
                    // Update the label text
                    const labelText = this.deviceControlStatusDiv.childNodes[0];
                    if (labelText && labelText.nodeType === Node.TEXT_NODE) {
                        labelText.textContent = "Control Key: ";
                    }
                }

                this.deviceControlStatusText.value = IntifaceController.instance.listenKey as string;
                this.deviceControlStatusDiv.style.display = '';
            } else {
                // Hide the display if permission not granted
                const existingDiv = document.getElementById('mm-device-permit-status-container') as HTMLDivElement;
                if (existingDiv) {
                    existingDiv.style.display = 'none';
                }
            }
        }
    }


    cleanupDeviceControls(): void {
        // This method clones & replaces all inputs to clear registered listeners
        if (this.durationCountdownTimer) {
            clearInterval(this.durationCountdownTimer);
            this.durationCountdownTimer = undefined;
        }
        this.durationCountdownInput = undefined;
        this.deviceControlDivs.forEach(div => {
            const inputs = div.querySelectorAll('input');
            inputs.forEach(input => {
                const clone = input.cloneNode(true); // true = deep clone attributes
                input.replaceWith(clone); // replaces old input with the clone
            });
        });

        if (this.deviceControlsModal) {
            this.deviceControlsModal.innerHTML = "";
        }

        this.deviceControlDivs = [];
        this.controllableDevicesMap = new Map<ButtplugClientDevice, Map<SupportedAction, HTMLInputElement[]>>();
        this.controllableAddonMap = new Map<ButtplugClientDevice, Map<SupportedAction, HTMLInputElement[]>>();
        this.requestedDurationInput = undefined;
    }

    sendDeviceCommands() {
        Logger.debug(MODULE, "Sending device commands");
        if (this.controllableDevicesMap) {
            //foreach device
            this.controllableDevicesMap.forEach((value, key) => {
                Logger.debug(MODULE, "Sending commands for device: " + key.name);
                let commandMap = value;
                commandMap.forEach((inputElems, actType) => {
                    Logger.debug(MODULE, "Processing action " + actType + " for device " + key.index);
                    let valArray: string[] = [];
                    let modArray: boolean[] | undefined;
                    switch (actType) {
                        case SupportedAction.Vibrate:
                            for (let i = 0; i < inputElems.length; i++) {
                                valArray[i] = inputElems[i].value;
                            }

                            break;
                        case SupportedAction.Scalar:
                            for (let i = 0; i < inputElems.length; i++) {
                                valArray[i] = inputElems[i].value;
                            }

                            break;
                        case SupportedAction.Rotate:
                            modArray = [];
                            let addonArray: HTMLInputElement[] | undefined = this.controllableAddonMap?.get(key)?.get(SupportedAction.Rotate);
                            for (let i = 0; i < inputElems.length; i++) {
                                valArray[i] = inputElems[i].value;
                                let clockwise: boolean = true;
                                if (addonArray)
                                    clockwise = addonArray[i].checked;
                                Logger.debug(MODULE, "Rotation clockwise for device " + key.index + " actuator " + i + " is " + clockwise);
                                modArray[i] = clockwise;
                            }

                            break;
                    }

                    let duration: string | undefined = undefined;
                    if (this.requestedDurationInput) {
                        const v = this.requestedDurationInput.value.trim();
                        if (v !== '') duration = v;
                    }
                    if(this.isRemote && this.peerId !== undefined) {
                        MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.sendRemoteDeviceCommand, { peerId: this.peerId, deviceId: key.index, actuateType: actType, actuateVals: valArray, duration: duration, modVals: modArray });
                    } else {
                        MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.sendDeviceCommand, { deviceId: key.index, actuateType: actType, actuateVals: valArray, duration: duration, modVals: modArray });
                    }
                });
            });
            MenuController.instance.updateDeviceControls();
        }
    }

    stopDeviceCommands() {
        if (this.controllableDevicesMap) {
            this.controllableDevicesMap.forEach((value, key) => {
                // key is a device (local or stub); use index
                const deviceId = (key as any).index;
                if (this.isRemote && this.peerId !== undefined) {
                    MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.stopRemoteDeviceCommand, { peerId: this.peerId, deviceId: deviceId });
                } else {
                    MenuController.instance.onUserChoice.notify(DEVICE_MENU_ACTIONS.stopDeviceCommand, { deviceId: deviceId });
                }
            });
        }
    }

    private setupPermissionListener() {
        const onPermissionEvent = async (e: any) => {
            try {
                switch (e.type) {
                    case Events.PLAYER_PERMITTING_DEVICECONTROL_SUCCESS:
                        Logger.info(MODULE, 'Device control permission granted');
                        this.permissionGranted = true;
                        this.updateConditionals();
                        break;
                    case Events.PLAYER_REVOKING_DEVICECONTROL_PERMISSION_SUCCESS:
                        Logger.info(MODULE, 'Device control permission revoked');
                        this.permissionGranted = false;
                        // Reset device control mode to Self and disconnect receiver
                        await IntifaceController.instance.disconnectRemoteReceiver();
                        // Hide the control key display
                        if (this.deviceControlStatusDiv) {
                            this.deviceControlStatusDiv.style.display = 'none';
                        }
                        this.updateConditionals();
                        this.updateDeviceControls();
                        break;
                    case Events.PLAYER_REVOKING_DEVICECONTROL_PERMISSION_FAILURE:
                        Logger.warn(MODULE, 'Failed to revoke device control permission');
                        break;
                }
            } catch (err) {
                // ignore malformed messages
            }
        };
        networkManager.onEvent.add(onPermissionEvent);
    }

    private revokeDeviceControl() {
        Logger.info(MODULE, 'Revoking device control permission');
        networkManager.client?.sendViaWs({
            type: Events.PLAYER_REVOKING_DEVICECONTROL_PERMISSION,
            data: '',
        });
    }

    private async disconnectController() {
        Logger.info(MODULE, 'Disconnecting remote controller');
        await IntifaceController.instance.disconnectRemoteReceiver();
        Logger.info(MODULE, 'Remote controller disconnected, updating UI');
        this.permissionGranted = false;
        this.updateConditionals();
        this.updateDeviceControls();
    }

    hideContent(): void {
        this.cleanupDeviceControls();
        this.removeAllEventListeners();

        this.deviceControlsModal = undefined;
    }

    /**
     * Get last command issued for a device and actuator type.
     * Works for both remote and local devices with unified interface.
     * 
     * @param device - Device object (local ButtplugClientDevice or remote DeviceRemoteStub)
     * @param actionType - Actuator type to query (Vibrate, Rotate, Linear, Oscillate, Scalar)
     * @returns Last DeviceRemoteCommand for this device/action, or null if none exists
     * 
     * @remarks
     * Lookup strategy:
     * - Remote devices (DeviceRemoteStub): Calls device.getLastCommand(actionType)
     * - Local devices (ButtplugClientDevice): Queries IntifaceController.deviceLastCommandMap
     * 
     * Returns null if:
     * - No command has been issued for this device/action
     * - Device is disconnected or no longer tracked
     * - Error occurs during lookup (logged as debug)
     * 
     * Used by UI generation to restore control values to last command state.
     */
    private getLastCommand(device: ButtplugClientDevice | DeviceRemoteStub, actionType: SupportedAction): any {
        try {
            if ((device instanceof DeviceRemoteStub) && (device as DeviceRemoteStub).getLastCommand(actionType) !== undefined) {
                return (device as DeviceRemoteStub).getLastCommand(actionType);
            } else {
                return IntifaceController.instance.deviceLastCommandMap.get((device as any).index)?.get(actionType);
            }
        } catch (err) {
            Logger.debug(MODULE, `Could not read last ${actionType} command for device`, err);
            return null;
        }
    }

    /**
     * Calculate latest end time from a timed command's duration.
     * Used to determine countdown timer display in device controls footer.
     * 
     * @param lastCmd - Last command object (should have duration and startMs properties)
     * @param currentLatestMs - Current latest end time across all devices/actions
     * @returns Updated latest end time in milliseconds, or currentLatestMs if this command ends sooner
     * 
     * @remarks
     * Calculation logic:
     * 1. Validates lastCmd has valid duration (number > 0) and startMs timestamp
     * 2. Calculates endMs = startMs + (duration * 1000)
     * 3. Returns endMs only if:
     *    - Command hasn't expired yet (endMs > Date.now())
     *    - Command ends later than current latest (endMs > currentLatestMs)
     * 4. Otherwise returns currentLatestMs unchanged
     * 
     * Used during UI generation to find which timed command ends last,
     * so a single countdown can be displayed showing time remaining.
     */
    private updateLatestEndMs(lastCmd: any, currentLatestMs: number): number {
        if (lastCmd && lastCmd.duration && !isNaN(parseFloat(lastCmd.duration)) && parseFloat(lastCmd.duration) > 0 && (lastCmd.startMs !== undefined)) {
            const endMs = (lastCmd.startMs as number) + parseFloat(lastCmd.duration) * 1000;
            if (endMs > Date.now() && endMs > currentLatestMs) {
                return endMs;
            }
        }
        return currentLatestMs;
    }

    /**
     * Create a range slider control with synchronized text input field.
     * Both controls stay in sync via bidirectional event listeners.
     * 
     * @param label - Display label for the control (e.g., "Vibe", "Rotate", "Linear")
     * @param maxValue - Maximum value for range slider (device's StepCount)
     * @param initialValue - Initial value for both range and text inputs
     * @returns Object containing the container div and text input reference
     * 
     * @remarks
     * Generated HTML structure:
     * ```html
     * <div class="mm-device-range-control">
     *   {label}
     *   <input type="range" min="0" max="{maxValue}" value="{initialValue}" />
     *   <input type="text" value="{initialValue}" />
     * </div>
     * ```
     * 
     * Synchronization behavior:
     * - Range input change → updates text input value
     * - Text input change → updates range input value (if valid number)
     * - Invalid text input is ignored, range maintains last valid value
     * 
     * The text input reference is returned for storing in controllableDevicesMap
     * so values can be read when Send button is clicked.
     */
    private createRangeControl(label: string, index: number,maxValue: string, initialValue: string): { div: HTMLDivElement, text: HTMLInputElement } {
        const div = document.createElement('div');
        div.innerHTML = label;
        div.classList.add('mm-device-range-control');

        const range = document.createElement('input');
        range.type = 'range';
        range.min = '0';
        range.max = maxValue;
        range.value = initialValue;
        range.id = `mm-device-range-slider-${label}-${index}`;

        const text = document.createElement('input');
        text.type = 'text';
        text.value = initialValue;
        text.id = `mm-device-range-text-${label}-${index}`;

        // Update text field when range changes
        range.addEventListener('input', () => {
            text.value = range.value;
        });

        // Update range when text is edited
        text.addEventListener('input', () => {
            const num = parseFloat(text.value);
            if (!isNaN(num)) range.value = "" + num;
        });

        div.appendChild(range);
        div.appendChild(text);

        return { div, text };
    }

    /**
     * Create vibrate controls for a device.
     * Returns updated latestEndMs if a timed command is active.
     */
    private createVibrateControls(device: ButtplugClientDevice | DeviceRemoteStub, latestEndMs: number): number {
        const vibeCount = IntifaceController.instance.getActuatorCount(device, SupportedAction.Vibrate);
        const inputArray: HTMLInputElement[] = [];

        const lastVibeCmd = this.getLastCommand(device, SupportedAction.Vibrate);
        latestEndMs = this.updateLatestEndMs(lastVibeCmd, latestEndMs);

        for (let i: number = 0; i < vibeCount; i++) {
            const initialValue = (lastVibeCmd && lastVibeCmd.actuateVals && lastVibeCmd.actuateVals[i] !== undefined) ? lastVibeCmd.actuateVals[i] : '0';
            const maxValue = "" + IntifaceController.instance.getVibeSteps(device, i);
            
            const control = this.createRangeControl("Vibe", i, maxValue, initialValue);
            
            this.deviceControlsModal?.appendChild(control.div);
            this.deviceControlDivs.push(control.div);
            inputArray[i] = control.text;
        }

        if (inputArray.length > 0) {
            this.controllableDevicesMap?.get(device)?.set(SupportedAction.Vibrate, inputArray);
        }

        return latestEndMs;
    }

    /**
     * Create linear controls for a device.
     * Returns updated latestEndMs if a timed command is active.
     */
    private createLinearControls(device: ButtplugClientDevice | DeviceRemoteStub, latestEndMs: number): number {
        if (!device.linearAttributes) return latestEndMs;
        
        const linearCount = device.linearAttributes.length;
        const inputArray: HTMLInputElement[] = [];

        const lastLinCmd = this.getLastCommand(device, SupportedAction.Linear);
        latestEndMs = this.updateLatestEndMs(lastLinCmd, latestEndMs);

        for (let i: number = 0; i < linearCount; i++) {
            const initialValue = (lastLinCmd && lastLinCmd.actuateVals && lastLinCmd.actuateVals[i] !== undefined) ? lastLinCmd.actuateVals[i] : '0';
            const maxValue = "" + device.linearAttributes[i].StepCount;
            
            const control = this.createRangeControl("Linear", i, maxValue, initialValue);
            
            this.deviceControlsModal?.appendChild(control.div);
            this.deviceControlDivs.push(control.div);
            inputArray[i] = control.text;
        }

        if (inputArray.length > 0) {
            this.controllableDevicesMap?.get(device)?.set(SupportedAction.Linear, inputArray);
        }

        return latestEndMs;
    }

    /**
     * Create oscillate controls for a device.
     * Returns updated latestEndMs if a timed command is active.
     */
    private createOscillateControls(device: ButtplugClientDevice | DeviceRemoteStub, latestEndMs: number): number {
        if (!device.oscillateAttributes) return latestEndMs;
        
        const osCount = device.oscillateAttributes.length;
        const inputArray: HTMLInputElement[] = [];

        const lastOsCmd = this.getLastCommand(device, SupportedAction.Oscillate);
        latestEndMs = this.updateLatestEndMs(lastOsCmd, latestEndMs);

        for (let i: number = 0; i < osCount; i++) {
            const initialValue = (lastOsCmd && lastOsCmd.actuateVals && lastOsCmd.actuateVals[i] !== undefined) ? lastOsCmd.actuateVals[i] : '0';
            const maxValue = "" + device.oscillateAttributes[i].StepCount;
            
            const control = this.createRangeControl("Oscillate", i, maxValue, initialValue);
            
            this.deviceControlsModal?.appendChild(control.div);
            this.deviceControlDivs.push(control.div);
            inputArray[i] = control.text;
        }

        if (inputArray.length > 0) {
            this.controllableDevicesMap?.get(device)?.set(SupportedAction.Oscillate, inputArray);
        }

        return latestEndMs;
    }

    /**
     * Create rotate controls for a device (includes clockwise checkbox).
     * Returns updated latestEndMs if a timed command is active.
     */
    private createRotateControls(device: ButtplugClientDevice | DeviceRemoteStub, latestEndMs: number): number {
        const rotateCount = IntifaceController.instance.getActuatorCount(device, SupportedAction.Rotate);
        const inputArray: HTMLInputElement[] = [];
        const addonArray: HTMLInputElement[] = [];

        const lastRotCmd = this.getLastCommand(device, SupportedAction.Rotate);
        latestEndMs = this.updateLatestEndMs(lastRotCmd, latestEndMs);

        for (let i: number = 0; i < rotateCount; i++) {
            const div = document.createElement('div');
            if (device.rotateAttributes) {
                div.innerHTML = device.rotateAttributes[i].ActuatorType;
            }
            div.classList.add('mm-device-range-control');

            const range = document.createElement('input');
            range.type = 'range';
            range.min = '0';
            range.max = "" + IntifaceController.instance.getRotateSteps(device, i);
            const initialValue = (lastRotCmd && lastRotCmd.actuateVals && lastRotCmd.actuateVals[i] !== undefined) ? lastRotCmd.actuateVals[i] : '0';
            range.value = initialValue;
            range.id = `mm-device-range-slider-${device.name}-${i}`;

            const text = document.createElement('input');
            text.type = 'text';
            text.value = initialValue;
            text.id = `mm-device-range-text-${device.name}-${i}`;

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            const initialClockwise = (lastRotCmd && lastRotCmd.modVals && lastRotCmd.modVals[i] !== undefined) ? lastRotCmd.modVals[i] : true;
            checkbox.checked = initialClockwise;
            checkbox.id = `mm-device-clockwise-checkbox-${device.name}-${i}`;

            const label = document.createElement('label');
            label.className = 'mm-clockwise-label';
            label.htmlFor = checkbox.id;
            label.textContent = initialClockwise ? 'Clockwise' : 'Counterclockwise';

            // Update label text when checkbox changes
            checkbox.addEventListener('change', () => {
                label.textContent = checkbox.checked ? 'Clockwise' : 'Counterclockwise';
            });

            // Update text field when range changes
            range.addEventListener('input', () => {
                text.value = range.value;
            });

            // Update range when text is edited
            text.addEventListener('input', () => {
                const num = parseFloat(text.value);
                if (!isNaN(num)) range.value = "" + num;
            });

            div.appendChild(range);
            div.appendChild(text);
            div.appendChild(checkbox);
            div.appendChild(label);

            this.deviceControlsModal?.appendChild(div);
            this.deviceControlDivs.push(div);
            inputArray[i] = text;
            addonArray[i] = checkbox;
        }

        if (inputArray.length > 0) {
            this.controllableDevicesMap?.get(device)?.set(SupportedAction.Rotate, inputArray);
            this.controllableAddonMap?.get(device)?.set(SupportedAction.Rotate, addonArray);
        }

        return latestEndMs;
    }

    /**
     * Create scalar controls for a device.
     * Returns updated latestEndMs if a timed command is active.
     */
    private createScalarControls(device: ButtplugClientDevice | DeviceRemoteStub, latestEndMs: number): number {
        const scalarAtts: GenericDeviceMessageAttributes[] = IntifaceController.instance.getNonvibScalarAttributes(device);
        const scaleCount = scalarAtts.length;
        const inputArray: HTMLInputElement[] = [];

        const lastScaleCmd = this.getLastCommand(device, SupportedAction.Scalar);
        latestEndMs = this.updateLatestEndMs(lastScaleCmd, latestEndMs);

        for (let i: number = 0; i < scaleCount; i++) {
            const initialValue = (lastScaleCmd && lastScaleCmd.actuateVals && lastScaleCmd.actuateVals[i] !== undefined) ? lastScaleCmd.actuateVals[i] : '0';
            const maxValue = "" + IntifaceController.instance.getScaleSteps(device, i);
            
            const control = this.createRangeControl(scalarAtts[i].ActuatorType, i, maxValue, initialValue);
            
            this.deviceControlsModal?.appendChild(control.div);
            this.deviceControlDivs.push(control.div);
            inputArray[i] = control.text;
        }

        if (inputArray.length > 0) {
            this.controllableDevicesMap?.get(device)?.set(SupportedAction.Scalar, inputArray);
        }

        return latestEndMs;
    }
}
